(function () {
  'use strict';

  const DEFAULT_MUSIC_PROMPT =
    'Warm lowfi beat with dusty drums, mellow Rhodes, and a subtle tape wobble for a creator intro';

  const state = {
    jobId: '',
    audioUrl: '',
    title: '',
    pollTimer: 0,
    result: null,
    timelineTrack: null,
    timelineAudio: null,
    previewAudio: null,
    waveformCache: new Map(),
  };

  function getEl(id) {
    return document.getElementById(id);
  }

  function getAuthSecret() {
    const user = window.currentUser || window.userData || {};
    return (
      user.secret ||
      user.api_key ||
      (user.user && (user.user.secret || user.user.api_key)) ||
      ''
    );
  }

  function buildHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    const secret = getAuthSecret();
    if (secret) {
      headers.Authorization = `Bearer ${secret}`;
      headers['X-API-Key'] = secret;
    }
    return headers;
  }

  function setStatus(message, tone) {
    const el = getEl('music-status');
    if (!el) return;
    el.textContent = message || '';
    el.className = 'mg-status';
    if (tone) el.classList.add(tone);
  }

  function setSearchStatus(message, tone) {
    const el = getEl('music-search-status');
    if (!el) return;
    el.textContent = message || '';
    el.className = 'mg-status';
    if (tone) el.classList.add(tone);
  }

  function toggleBusy(busy) {
    const button = getEl('music-generate');
    if (!button) return;
    button.disabled = !!busy;
    button.textContent = busy ? 'Generating…' : 'Generate Music ($0.50)';
  }

  function formatTime(value) {
    const seconds = Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
  }

  function mediaURL(source) {
    if (!source) return '';
    if (source.startsWith('https://netwrckstatic.netwrck.com/static/audio/library/')) {
      return `/api/audio-library-proxy?url=${encodeURIComponent(source)}`;
    }
    return source;
  }

  function handleAuthFailure() {
    if (window.NetwrckTools && typeof window.NetwrckTools.requireLogin === 'function') {
      window.NetwrckTools.requireLogin();
      return;
    }
    if (typeof window.showSignupDialog === 'function') {
      window.showSignupDialog(null, '/tools/music-generator');
      return;
    }
    window.location.href =
      '/login?next=' + encodeURIComponent(window.location.pathname + window.location.search);
  }

  function fallbackPeaks(label, count) {
    let seed = 2166136261;
    for (const char of label || 'music') {
      seed ^= char.charCodeAt(0);
      seed = Math.imul(seed, 16777619);
    }
    return Array.from({ length: count }, function (_, index) {
      seed = Math.imul(seed ^ index, 1664525) + 1013904223;
      return 0.18 + (Math.abs(seed) % 760) / 1000;
    });
  }

  async function audioPeaks(url, label, count) {
    const key = `${url}:${count}`;
    if (state.waveformCache.has(key)) return state.waveformCache.get(key);
    try {
      const response = await fetch(mediaURL(url), { credentials: 'same-origin' });
      if (!response.ok) throw new Error('audio unavailable');
      const context = new (window.AudioContext || window.webkitAudioContext)();
      const buffer = await context.decodeAudioData(await response.arrayBuffer());
      const channels = Array.from({ length: buffer.numberOfChannels }, function (_, index) {
        return buffer.getChannelData(index);
      });
      const block = Math.max(1, Math.floor(buffer.length / count));
      const peaks = Array.from({ length: count }, function (_, index) {
        const start = index * block;
        const end = Math.min(buffer.length, start + block);
        let peak = 0;
        for (let sample = start; sample < end; sample += Math.max(1, Math.floor(block / 64))) {
          for (const channel of channels) peak = Math.max(peak, Math.abs(channel[sample] || 0));
        }
        return Math.max(0.05, Math.min(1, peak));
      });
      context.close();
      state.waveformCache.set(key, peaks);
      return peaks;
    } catch {
      const peaks = fallbackPeaks(label, count);
      state.waveformCache.set(key, peaks);
      return peaks;
    }
  }

  function paintWaveform(canvas, peaks, progress) {
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(280, canvas.clientWidth || 640);
    const height = Math.max(54, canvas.clientHeight || 90);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.clearRect(0, 0, width, height);
    const gap = 2;
    const barWidth = Math.max(1, width / peaks.length - gap);
    peaks.forEach(function (peak, index) {
      const x = (index / peaks.length) * width;
      const barHeight = Math.max(3, peak * (height - 12));
      const played = index / peaks.length <= progress;
      const gradient = ctx.createLinearGradient(0, (height - barHeight) / 2, 0, (height + barHeight) / 2);
      gradient.addColorStop(0, played ? '#67e8f9' : '#64748b');
      gradient.addColorStop(1, played ? '#fb7185' : '#334155');
      ctx.fillStyle = gradient;
      ctx.fillRect(x, (height - barHeight) / 2, barWidth, barHeight);
    });
  }

  async function drawTrackWaveform(track) {
    const canvas = getEl('music-timeline-waveform');
    if (!canvas || !track) return;
    const peaks = await audioPeaks(track.sourceUrl, track.title, 160);
    if (state.timelineTrack !== track) return;
    track.peaks = peaks;
    paintWaveform(canvas, peaks, 0);
  }

  function updateTimelineProgress() {
    const audio = state.timelineAudio;
    const track = state.timelineTrack;
    if (!audio || !track) return;
    const duration = Number.isFinite(audio.duration) ? audio.duration : track.duration || 0;
    const progress = duration > 0 ? Math.min(1, audio.currentTime / duration) : 0;
    const scrubber = getEl('music-timeline-scrubber');
    const playhead = getEl('music-timeline-playhead');
    const current = getEl('music-timeline-current');
    if (scrubber) {
      scrubber.max = String(duration || 1);
      scrubber.value = String(audio.currentTime || 0);
    }
    if (playhead) playhead.style.left = `${progress * 100}%`;
    if (current) current.textContent = `${formatTime(audio.currentTime)} / ${formatTime(duration)}`;
    if (track.peaks) paintWaveform(getEl('music-timeline-waveform'), track.peaks, progress);
  }

  function setTimelineTrack(track) {
    if (!track || !track.sourceUrl) return;
    if (state.previewAudio) state.previewAudio.pause();
    if (state.timelineAudio) state.timelineAudio.pause();
    state.timelineTrack = track;
    const audio = new Audio(mediaURL(track.sourceUrl));
    audio.preload = 'metadata';
    audio.addEventListener('loadedmetadata', function () {
      if (Number.isFinite(audio.duration)) track.duration = audio.duration;
      updateTimelineProgress();
      updateTimelineRuler(track.duration);
    });
    audio.addEventListener('timeupdate', updateTimelineProgress);
    audio.addEventListener('play', function () {
      const button = getEl('music-timeline-play');
      if (button) button.textContent = 'Pause';
    });
    audio.addEventListener('pause', function () {
      const button = getEl('music-timeline-play');
      if (button) button.textContent = 'Play';
    });
    audio.addEventListener('ended', updateTimelineProgress);
    state.timelineAudio = audio;
    const empty = getEl('music-timeline-empty');
    const clip = getEl('music-timeline-clip');
    const name = getEl('music-timeline-name');
    const meta = getEl('music-timeline-meta');
    const editor = getEl('music-timeline-editor');
    if (empty) empty.hidden = true;
    if (clip) clip.hidden = false;
    if (name) name.textContent = track.title || 'Music track';
    if (meta) {
      const details = [track.provider, track.license, track.attribution, formatTime(track.duration)].filter(Boolean);
      meta.textContent = details.join(' · ');
    }
    if (editor) editor.disabled = false;
    updateTimelineRuler(track.duration);
    drawTrackWaveform(track);
    updateTimelineProgress();
  }

  function updateTimelineRuler(duration) {
    const ruler = getEl('music-timeline-ruler');
    if (!ruler) return;
    const total = Math.max(1, Number(duration) || 30);
    ruler.innerHTML = '';
    [0, 0.25, 0.5, 0.75, 1].forEach(function (position) {
      const marker = document.createElement('span');
      marker.style.left = `${position * 100}%`;
      marker.textContent = formatTime(total * position);
      ruler.appendChild(marker);
    });
  }

  function openTrackInEditor() {
    const track = state.timelineTrack;
    if (!track) return;
    window.open(`/tools/video-editor?audioUrl=${encodeURIComponent(track.sourceUrl)}&audioName=${encodeURIComponent(track.title || 'Music track')}`, '_blank');
  }

  async function saveGeneratedTrackToAccount() {
    if (!state.audioUrl) return false;
    try {
      const response = await fetch('/api/video-editor/assets/save', {
        method: 'POST',
        credentials: 'include',
        headers: buildHeaders(),
        body: JSON.stringify({ name: state.title || 'Generated Music', type: 'audio', src: state.audioUrl }),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  function updateResult(result) {
    state.result = result || null;
    state.audioUrl = (result && (result.audio_url || result.src)) || '';
    state.title = (result && (result.title || result.name)) || 'Generated Music';
    const card = getEl('music-result-card');
    const audio = getEl('music-result-audio');
    const title = getEl('music-result-title');
    const sub = getEl('music-result-sub');
    const download = getEl('music-download');
    const openEditor = getEl('music-open-editor');
    if (!card || !audio || !download || !openEditor) return;
    if (!state.audioUrl) {
      card.classList.remove('show');
      openEditor.disabled = true;
      return;
    }
    card.classList.add('show');
    audio.src = mediaURL(state.audioUrl);
    title.textContent = state.title;
    sub.textContent = 'Generated, indexed, and ready for the timeline.';
    download.href = state.audioUrl;
    download.download = `${state.title || 'generated-music'}.${(result && result.format) || 'wav'}`;
    openEditor.disabled = false;
    const track = {
      sourceUrl: state.audioUrl,
      title: state.title,
      duration: Number(result && result.duration) || Number(getEl('music-duration')?.value) || 0,
      provider: 'Netwrck generated',
      license: 'generated',
    };
    setTimelineTrack(track);
    audioPeaks(track.sourceUrl, track.title, 96).then(function (peaks) {
      paintWaveform(getEl('music-result-waveform'), peaks, 0);
    });
  }

  function normalizeSearchResults(payload) {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.results)) return payload.results;
    if (payload && Array.isArray(payload.items)) return payload.items;
    return [];
  }

  function previewSearchTrack(item, button) {
    if (state.previewAudio) {
      state.previewAudio.pause();
      if (state.previewAudio._button) state.previewAudio._button.textContent = 'Preview';
      if (state.previewAudio._item === item) {
        state.previewAudio = null;
        return;
      }
    }
    const audio = new Audio(mediaURL(item.url));
    audio._button = button;
    audio._item = item;
    audio.addEventListener('ended', function () {
      button.textContent = 'Preview';
      if (state.previewAudio === audio) state.previewAudio = null;
    });
    audio.addEventListener('error', function () {
      button.textContent = 'Unavailable';
    });
    state.previewAudio = audio;
    button.textContent = 'Pause';
    audio.play().catch(function () {
      button.textContent = 'Preview';
    });
  }

  function renderSearchResults(items) {
    const container = getEl('music-search-results');
    if (!container) return;
    container.innerHTML = '';
    items.forEach(function (item) {
      const card = document.createElement('article');
      card.className = 'mg-search-card';
      const visual = item.cover_url ? document.createElement('img') : document.createElement('canvas');
      visual.className = 'mg-search-wave';
      visual.setAttribute('aria-hidden', 'true');
      if (item.cover_url) {
        visual.src = mediaURL(item.cover_url);
        visual.alt = '';
        visual.loading = 'lazy';
      }
      const body = document.createElement('div');
      body.className = 'mg-search-body';
      const title = document.createElement('h3');
      title.textContent = item.title || 'Untitled track';
      const meta = document.createElement('p');
      meta.textContent = [item.provider, item.license, item.attribution, formatTime(item.duration)].filter(Boolean).join(' · ');
      const tags = document.createElement('p');
      tags.className = 'mg-search-tags';
      tags.textContent = Array.isArray(item.tags) ? item.tags.slice(0, 6).join(' · ') : '';
      const actions = document.createElement('div');
      actions.className = 'mg-actions';
      const preview = document.createElement('button');
      preview.className = 'mg-btn secondary';
      preview.type = 'button';
      preview.textContent = 'Preview';
      preview.setAttribute('aria-label', `Preview ${item.title || 'track'}`);
      preview.addEventListener('click', function () {
        previewSearchTrack(item, preview);
      });
      const add = document.createElement('button');
      add.className = 'mg-btn primary compact';
      add.type = 'button';
      add.textContent = 'Add to timeline';
      add.addEventListener('click', function () {
        setTimelineTrack({
          sourceUrl: item.url,
          title: item.title || 'Music track',
          duration: Number(item.duration) || 0,
          provider: item.provider,
          license: item.license,
          attribution: item.attribution,
        });
        setSearchStatus(`${item.title || 'Track'} added to timeline.`, 'success');
        getEl('music-timeline')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      actions.append(preview, add);
      body.append(title, meta, tags, actions);
      card.append(visual, body);
      container.appendChild(card);
      if (!item.cover_url) paintWaveform(visual, fallbackPeaks(item.title, 72), 0);
    });
  }

  var searchKind = 'music';

  async function searchMusic() {
    const query = (getEl('music-search-query')?.value || '').trim();
    const button = getEl('music-search-submit');
    if (button) button.disabled = true;
    setSearchStatus('Searching the licensed catalog…', '');
    try {
      const response = await fetch(`/api/search-audio?query=${encodeURIComponent(query)}&kind=${encodeURIComponent(searchKind)}&limit=12`, {
        credentials: 'same-origin',
      });
      const data = await response.json().catch(function () { return []; });
      if (!response.ok) throw new Error(data.error || 'Search failed');
      const items = normalizeSearchResults(data).filter(function (item) { return item && item.url; });
      renderSearchResults(items);
      setSearchStatus(items.length ? `${items.length} tracks found.` : 'No tracks found. Try a mood, instrument, or scene.', items.length ? 'success' : '');
    } catch (error) {
      setSearchStatus(error.message || 'Search failed.', 'error');
    } finally {
      if (button) button.disabled = false;
    }
  }

  async function pollJob(jobId) {
    if (!jobId) return;
    try {
      const response = await fetch(`/api/music-generator/status/${encodeURIComponent(jobId)}`, {
        credentials: 'include', headers: buildHeaders(),
      });
      if (response.status === 401) {
        toggleBusy(false);
        setStatus('Sign in required.', 'error');
        return;
      }
      const data = await response.json().catch(function () { return {}; });
      if (!response.ok) {
        toggleBusy(false);
        setStatus(data.error || 'Generation failed.', 'error');
        return;
      }
      if (data.status === 'completed') {
        window.clearTimeout(state.pollTimer);
        toggleBusy(false);
        updateResult(data.result || {});
        const saved = await saveGeneratedTrackToAccount();
        setStatus(saved ? 'Track generated and saved to your library.' : 'Track generated.', 'success');
        return;
      }
      if (data.status === 'failed') {
        window.clearTimeout(state.pollTimer);
        toggleBusy(false);
        setStatus(data.error || 'Generation failed.', 'error');
        return;
      }
      const progress = typeof data.progress === 'number' ? ` ${Math.max(0, Math.min(100, data.progress))}%` : '';
      setStatus(`Generating music…${progress}`, '');
      state.pollTimer = window.setTimeout(function () { pollJob(jobId); }, 3000);
    } catch {
      toggleBusy(false);
      setStatus('Generation failed.', 'error');
    }
  }

  async function submitGeneration() {
    const prompt = (getEl('music-prompt')?.value || DEFAULT_MUSIC_PROMPT).trim();
    const duration = parseInt(getEl('music-duration')?.value || '12', 10) || 12;
    const format = (getEl('music-format')?.value || 'wav').trim();
    const seedRaw = (getEl('music-seed')?.value || '').trim();
    toggleBusy(true);
    setStatus('Submitting music job…', '');
    window.clearTimeout(state.pollTimer);
    const kind = (getEl('music-kind')?.value || 'music').trim();
    const payload = { prompt, duration, format, title: prompt.slice(0, 64), is_instrumental: true, kind };
    if (seedRaw) payload.seed = parseInt(seedRaw, 10) || 0;
    try {
      const response = await fetch('/api/music-generator', {
        method: 'POST', credentials: 'include', headers: buildHeaders(), body: JSON.stringify(payload),
      });
      const data = await response.json().catch(function () { return {}; });
      if (response.status === 401) {
        toggleBusy(false);
        handleAuthFailure();
        return;
      }
      if (!response.ok) {
        toggleBusy(false);
        setStatus(data.error || 'Unable to start generation.', 'error');
        return;
      }
      if (data.status === 'completed') {
        toggleBusy(false);
        updateResult(data.result || {});
        const saved = await saveGeneratedTrackToAccount();
        setStatus(data.cached ? 'Cached track loaded.' : saved ? 'Track generated and saved to your library.' : 'Track generated.', 'success');
        return;
      }
      state.jobId = data.job_id || '';
      setStatus('Music job started.', '');
      if (state.jobId) pollJob(state.jobId);
      else {
        toggleBusy(false);
        setStatus('Missing job id.', 'error');
      }
    } catch {
      toggleBusy(false);
      setStatus('Unable to start generation.', 'error');
    }
  }

  function bindEvents() {
    getEl('music-generate')?.addEventListener('click', submitGeneration);
    getEl('music-prompt')?.addEventListener('keydown', function (event) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        submitGeneration();
      }
    });
    getEl('music-open-editor')?.addEventListener('click', openTrackInEditor);
    getEl('music-timeline-editor')?.addEventListener('click', openTrackInEditor);
    getEl('music-save-account')?.addEventListener('click', async function () {
      const ok = await saveGeneratedTrackToAccount();
      setStatus(ok ? 'Saved to your library.' : 'Could not save to your library.', ok ? 'success' : 'error');
    });
    document.querySelectorAll('#music-kind-chips [data-music-kind]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        searchKind = chip.getAttribute('data-music-kind') || 'music';
        document.querySelectorAll('#music-kind-chips [data-music-kind]').forEach(function (other) {
          if (other === chip) other.setAttribute('data-active', 'true');
          else other.removeAttribute('data-active');
        });
        searchMusic();
      });
    });
    getEl('music-search-submit')?.addEventListener('click', searchMusic);
    getEl('music-search-query')?.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        searchMusic();
      }
    });
    getEl('music-timeline-play')?.addEventListener('click', function () {
      if (!state.timelineAudio) return;
      if (state.timelineAudio.paused) state.timelineAudio.play().catch(function () {});
      else state.timelineAudio.pause();
    });
    getEl('music-timeline-scrubber')?.addEventListener('input', function (event) {
      if (!state.timelineAudio) return;
      state.timelineAudio.currentTime = Number(event.target.value) || 0;
      updateTimelineProgress();
    });
    window.addEventListener('resize', function () {
      const track = state.timelineTrack;
      if (track?.peaks) updateTimelineProgress();
    });
  }

  function init() {
    const prompt = getEl('music-prompt');
    if (prompt && !prompt.value.trim()) prompt.value = DEFAULT_MUSIC_PROMPT;
    updateTimelineRuler(30);
    bindEvents();
    searchMusic();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
